import React, { useState, useEffect } from 'react';
import { CheckSquare, Square, File, Database, FileText, Clock, Trash2 } from 'lucide-react';
import { FieldOverrides, ComplianceOverrides, FieldSource } from '../services/types';
import { formatDateTime } from '../utils/formatDate';

type ComplianceStatus = 'compliant' | 'non-compliant';

/**
 * Convert string compliance value to boolean interpretation.
 * Used for loading saved compliance overrides.
 */
const isCompliantStringValue = (value: string | undefined): boolean | undefined => {
  if (value === undefined) return undefined;
  const lowerVal = value.toLowerCase();
  // Values that indicate compliance/positive status
  if (['compliant', 'current', 'closed', 'created', 'true'].includes(lowerVal)) {
    return true;
  }
  // Values that indicate non-compliance/negative/neutral status
  if (['non_compliant', 'does_not_comply', 'under_review', 'open', 'na', 'n/a', 'false'].includes(lowerVal)) {
    return false;
  }
  // Default to undefined for unknown values
  return undefined;
};

/**
 * Get color classes for source badge based on source type
 */
const getSourceBadgeColor = (source: string): string => {
  const sourceLower = source.toLowerCase();

  if (sourceLower.includes('sap')) {
    return 'bg-blue-100 text-blue-800 border-blue-200';
  }
  if (sourceLower.includes('cara')) {
    return 'bg-green-100 text-green-800 border-green-200';
  }
  if (sourceLower.includes('document') || sourceLower.includes('uploaded')) {
    return 'bg-purple-100 text-purple-800 border-purple-200';
  }
  if (sourceLower.includes('manual') || sourceLower.includes('edit')) {
    return 'bg-orange-100 text-orange-800 border-orange-200';
  }
  if (sourceLower.includes('qaa')) {
    return 'bg-indigo-100 text-indigo-800 border-indigo-200';
  }
  if (sourceLower.includes('artwork') || sourceLower.includes('kau')) {
    return 'bg-pink-100 text-pink-800 border-pink-200';
  }
  if (sourceLower.includes('box') || sourceLower.includes('cmo')) {
    return 'bg-cyan-100 text-cyan-800 border-cyan-200';
  }

  // Default
  return 'bg-gray-100 text-gray-800 border-gray-200';
};

interface ValidationNote {
  text: string;
  timestamp: string;
}

interface ValidatedFieldWithComplianceProps {
  label: string;
  description?: string;
  brrValue: string | boolean;
  sourceValue: string | boolean | Record<string, string | boolean>;
  source: string;
  isCheckbox?: boolean;
  sourceOnly?: boolean;
  fieldId: string;
  onComplianceChange: (fieldId: string, isCompliant: boolean) => void;
  brrSourceDoc?: string;
  fieldOverrides: FieldOverrides;
  setFieldOverrides: React.Dispatch<React.SetStateAction<FieldOverrides>>;
  complianceOverrides: ComplianceOverrides;
  defaultNonCompliant?: boolean;
  subtitle?: string;
  displayFormatter?: (value: string | boolean) => string;
}

export const ValidatedFieldWithCompliance = React.memo(({
  label,
  description,
  brrValue,
  sourceValue,
  source,
  isCheckbox = false,
  sourceOnly = false,
  fieldId,
  onComplianceChange,
  brrSourceDoc = 'Uploaded Documents',
  fieldOverrides,
  setFieldOverrides,
  complianceOverrides,
  defaultNonCompliant = false,
  subtitle,
  displayFormatter
}: ValidatedFieldWithComplianceProps) => {
  // Check if there's an existing override for this field
  const existingOverride = fieldOverrides?.[fieldId];
  const existingComplianceOverride = complianceOverrides?.[fieldId];

  const [notes, setNotes] = useState('');
  const [showNotes, setShowNotes] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedValue, setEditedValue] = useState(existingOverride?.value || brrValue);
  const [hasBeenEdited, setHasBeenEdited] = useState(
    !!existingOverride && existingOverride.value !== existingOverride.originalValue
  );
  const [currentNoteText, setCurrentNoteText] = useState('');
  // Store validation notes as an array of objects: [{text: string, timestamp: Date}, ...]
  const [validationNotesArray, setValidationNotesArray] = useState(existingOverride?.validationNotesArray || []);

  const handleSourceValueClick = (value: string | boolean) => {
    if (isCheckbox) return;
    setEditedValue(value);
    setHasBeenEdited(true);
    setIsEditing(false);
    if (setFieldOverrides) {
      setFieldOverrides(prev => ({
        ...prev,
        [fieldId]: {
          ...prev[fieldId],
          value: value,
          originalValue: prev[fieldId]?.originalValue ?? brrValue,
          validationNotesArray: prev[fieldId]?.validationNotesArray || [],
          source: 'Manual'
        }
      }));
    }
  };

  // Sync editedValue when brrValue or field override changes (but not while user is editing)
  useEffect(() => {
    if (!isEditing) {
      const currentOverride = fieldOverrides?.[fieldId];
      const newValue = currentOverride?.value || brrValue;
      setEditedValue(newValue);
      setHasBeenEdited(
        !!currentOverride && currentOverride.value !== currentOverride.originalValue
      );
    }
  }, [brrValue, fieldOverrides, fieldId, isEditing]);

  // Handle source as either string or array, and split by " / " if it's a string with multiple sources
  const sources = Array.isArray(source)
    ? source
    : (typeof source === 'string' ? source.split(' / ').map(s => s.trim()) : [source]);
  const [selectedSource, setSelectedSource] = useState(sources[0]);

  // Get the appropriate source value based on selected source
  // sourceValue can be a single value or an object mapping source names to values
  const getSourceValue = (): string | boolean => {
    if (typeof sourceValue === 'object' && !Array.isArray(sourceValue) && sourceValue !== null) {
      // For checkbox values, handle boolean lookups
      if (isCheckbox && typeof sourceValue[selectedSource] === 'boolean') {
        return sourceValue[selectedSource];
      }
      // For regular values
      return sourceValue[selectedSource] !== undefined ? sourceValue[selectedSource] : '';
    }
    return sourceValue as string | boolean;
  };

  const displaySourceValue = getSourceValue();

  // Helper to check if a value is empty or an error message
  // All error messages should follow the standardized format: "No {field} found"
  const isEmptyOrError = (val: string | boolean): boolean => {
    if (typeof val === 'boolean') return false;
    const str = String(val || '').trim().toLowerCase();
    if (str === '') return true;

    // Primary pattern: standardized error messages "No ... found"
    // Examples: "No Marketing Authorisation found", "No API Name found", "No Valid To Date found"
    if (str.startsWith('no ') && str.includes(' found')) {
      return true;
    }

    // Safety net: catch common sentinel values that might slip through from backend
    const sentinelValues = ['n/a', 'null', 'none', 'undefined'];
    if (sentinelValues.includes(str)) {
      return true;
    }

    return false;
  };

  // Determine initial compliance based on whether values match ALL sources
  const determineCompliance = (valueToCheck = brrValue) => {
    // CRITICAL: For non-checkbox fields, if BRR value is empty or error message, cannot be compliant (no data to validate)
    if (!isCheckbox && isEmptyOrError(String(valueToCheck))) {
      return 'non-compliant';
    }

    // If there are multiple sources, check if they all agree
    if (sources.length > 1) {
      // Check if sourceValue is an object with multiple source mappings
      if (typeof sourceValue === 'object' && !Array.isArray(sourceValue) && sourceValue !== null) {
        // Check ALL sources - each source must match the BRR value AND be non-empty
        for (const src of sources) {
          const srcValue = sourceValue[src];
          if (isCheckbox) {
            if (valueToCheck !== srcValue) {
              return 'non-compliant';
            }
          } else {
            const brrStr = String(valueToCheck || '').trim().toLowerCase();
            const sourceStr = String(srcValue || '').trim().toLowerCase();

            // Non-compliant if: values don't match OR source value is empty/error
            if (brrStr !== sourceStr || isEmptyOrError(sourceStr)) {
              return 'non-compliant';
            }
          }
        }
        return 'compliant'; // All sources matched AND all have non-empty values
      } else {
        // Multiple sources from split string, but only one value
        // This means all sources should agree on this value
        // Compare the BRR value with the single source value
        if (isCheckbox) {
          return valueToCheck === sourceValue ? 'compliant' : 'non-compliant';
        }
        const brrStr = String(valueToCheck || '').trim().toLowerCase();
        const sourceStr = String(sourceValue || '').trim().toLowerCase();
        // Non-compliant if values don't match OR if source is empty/error
        return (brrStr === sourceStr && !isEmptyOrError(sourceStr)) ? 'compliant' : 'non-compliant';
      }
    }

    // Single source - just compare with current source value
    const currentSourceValue = getSourceValue();
    if (isCheckbox) {
      return valueToCheck === currentSourceValue ? 'compliant' : 'non-compliant';
    }
    // For string/number values, compare as strings after trimming
    const brrStr = String(valueToCheck || '').trim().toLowerCase();
    const sourceStr = String(currentSourceValue || '').trim().toLowerCase();
    // Non-compliant if values don't match OR if source is empty/error
    return (brrStr === sourceStr && !isEmptyOrError(sourceStr)) ? 'compliant' : 'non-compliant';
  };

  // Auto-calculate what compliance SHOULD be based on current values
  const autoCalculatedCompliance = React.useMemo<ComplianceStatus>(() => {
    // If defaultNonCompliant is set, always start as non-compliant
    if (defaultNonCompliant) {
      return 'non-compliant';
    }

    const valueToCheck = existingOverride?.value || brrValue;
    return determineCompliance(valueToCheck);
  }, [brrValue, sourceValue, existingOverride, defaultNonCompliant, fieldId]);

  // Track if user has manually overridden compliance
  // Initialize from saved complianceOverrides if present
  const [userOverrideValue, setUserOverrideValue] = useState<ComplianceStatus | null>(() => {
    const boolVal = isCompliantStringValue(existingComplianceOverride);
    if (boolVal !== undefined) {
      return boolVal ? 'compliant' : 'non-compliant';
    }
    return null;
  });

  // Sync userOverrideValue when complianceOverrides prop changes (e.g., after loadSavedState)
  const prevComplianceOverrideRef = React.useRef<string | undefined>(existingComplianceOverride);
  React.useEffect(() => {
    // Only update if the prop actually changed (not on every render)
    if (prevComplianceOverrideRef.current !== existingComplianceOverride && existingComplianceOverride !== undefined) {
      const boolVal = isCompliantStringValue(existingComplianceOverride);
      if (boolVal !== undefined) {
        setUserOverrideValue(boolVal ? 'compliant' : 'non-compliant');
      }
    }
    prevComplianceOverrideRef.current = existingComplianceOverride;
  }, [existingComplianceOverride]);

  // Displayed compliance: user override takes precedence, otherwise auto-calculated
  const compliance = userOverrideValue !== null ? userOverrideValue : autoCalculatedCompliance;

  const lastSentComplianceRef = React.useRef<boolean | null>(null);

  // Auto-open validation notes when field is manually edited
  React.useEffect(() => {
    if (hasBeenEdited && validationNotesArray.length === 0) {
      setShowNotes(true);
    }
  }, [hasBeenEdited, validationNotesArray.length]);

  // Sync validation notes state with fieldOverrides changes
  React.useEffect(() => {
    if (existingOverride?.validationNotesArray) {
      setValidationNotesArray(existingOverride.validationNotesArray);
    }
  }, [existingOverride?.validationNotesArray]);

  // Report initial compliance status to parent on mount
  React.useEffect(() => {
    const isCompliant = compliance === 'compliant';
    // Only report if we haven't sent this compliance status yet
    if (lastSentComplianceRef.current !== isCompliant && onComplianceChange) {
      lastSentComplianceRef.current = isCompliant;
      onComplianceChange(fieldId, isCompliant);
    }
  }, [compliance, fieldId, onComplianceChange]);

  const handleComplianceChange = (newCompliance: ComplianceStatus, isManual = false) => {
    const isCompliant = newCompliance === 'compliant';

    if (isManual) {
      // User manually clicked dropdown - set override
      setUserOverrideValue(newCompliance);
    }

    // IMPORTANT: Changing compliance status NEVER modifies the editedValue
    // The editedValue is preserved independently of validation status changes
    if (onComplianceChange) {
      lastSentComplianceRef.current = isCompliant;
      onComplianceChange(fieldId, isCompliant);
    }
  };

  return (
    <div id={`field-${fieldId}`} className="p-4 bg-white rounded-lg border border-gray-200 hover:border-green-300 transition-all">
      {/* Field Label */}
      {label && <div className="text-sm font-semibold text-gray-900 mb-1">{label}</div>}
      {description && (
        <div className="text-xs text-gray-600 mb-3 italic">{description}</div>
      )}

      {/* BRR Value and Source Value Side by Side (or Source-only mode) */}
      <div className={`${sourceOnly ? '' : 'grid grid-cols-2 gap-4'} mb-3`}>
        {/* BRR Field (Left) - hidden in sourceOnly mode */}
        {!sourceOnly && (
          <div className="p-3 bg-gray-50 rounded border border-gray-200">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-1">
                <div className="text-xs text-gray-500 font-medium">BRR Field Value</div>
                {hasBeenEdited && (
                  <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded font-medium">
                    Edited
                  </span>
                )}
              </div>
              {!isCheckbox && !isEditing && (
                <button
                  onClick={() => setIsEditing(true)}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                >
                  Edit
                </button>
              )}
            </div>
            {(typeof brrValue === 'boolean' || (typeof brrValue === 'string' && brrValue !== '' && !/^No .+ found$/.test(brrValue))) && (
              <div className="text-xs mb-2 flex items-center gap-2">
                <File size={10} className="text-gray-400" />
                <span className="text-gray-400">Source:</span>
                <span className={`px-2 py-0.5 rounded border text-xs font-medium ${getSourceBadgeColor(brrSourceDoc)}`}>
                  {brrSourceDoc}
                </span>
              </div>
            )}
            {isCheckbox ? (
              <div className="flex items-center gap-2">
                {editedValue ? (
                  <CheckSquare size={18} className="text-green-600" />
                ) : (
                  <Square size={18} className="text-gray-400" />
                )}
                <span className={editedValue ? 'text-green-700 font-medium' : 'text-gray-500'}>
                  {editedValue ? 'Yes' : 'No'}
                </span>
              </div>
            ) : isEditing ? (
              <div className="space-y-2">
                <input
                  type="text"
                  value={String(editedValue)}
                  onChange={(e) => setEditedValue(e.target.value)}
                  className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                  autoFocus
                />
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setHasBeenEdited(true);
                      setIsEditing(false);
                      setShowNotes(true);

                      // Compliance will auto-recalculate via useMemo when fieldOverrides changes

                      // Save to global field overrides
                      if (setFieldOverrides) {
                        setFieldOverrides(prev => ({
                          ...prev,
                          [fieldId]: {
                            value: editedValue,
                            originalValue: brrValue,
                            validationNotesArray: prev[fieldId]?.validationNotesArray || [],
                            source: 'Manual'
                          }
                        }));
                      }
                    }}
                    className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => {
                      setEditedValue(brrValue);
                      setIsEditing(false);
                    }}
                    className="px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs rounded"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className={
                String(editedValue).startsWith('No ') && String(editedValue).includes(' found')
                  ? "text-gray-400 text-xs break-words whitespace-normal"
                  : "text-gray-900 font-medium break-words whitespace-normal"
              }>
                {displayFormatter ? displayFormatter(editedValue) : editedValue}
              </div>
            )}
          </div>
        )}

        {/* Source of Truth (Right, or full-width in sourceOnly mode) */}
        <div className="p-3 bg-blue-50 rounded border border-blue-200">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <div className="text-xs font-medium flex items-center gap-2">
                <Database size={10} className="text-gray-500" />
                <span className="text-gray-500">Source:</span>
                {sources.length <= 1 && (
                  <span className={`px-2 py-0.5 rounded border text-xs font-medium ${getSourceBadgeColor(selectedSource)}`}>
                    {selectedSource}
                  </span>
                )}
              </div>
              {sourceOnly && hasBeenEdited && (
                <span className="px-1.5 py-0.5 bg-amber-100 text-amber-700 text-xs rounded font-medium">
                  Edited
                </span>
              )}
            </div>
            {sourceOnly && !isCheckbox && !isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="text-xs text-blue-600 hover:text-blue-700 font-medium"
              >
                Edit
              </button>
            )}
          </div>
          {/* Show editing UI for sourceOnly mode regardless of number of sources */}
          {sourceOnly && isEditing ? (
            <div className="space-y-2">
              <textarea
                value={String(editedValue)}
                onChange={(e) => setEditedValue(e.target.value)}
                className="w-full px-2 py-1 border border-blue-300 rounded text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                rows={4}
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setHasBeenEdited(true);
                    setIsEditing(false);
                    setShowNotes(true);

                    // Compliance will auto-recalculate via useMemo when fieldOverrides changes

                    if (setFieldOverrides) {
                      setFieldOverrides(prev => ({
                        ...prev,
                        [fieldId]: {
                          value: editedValue,
                          originalValue: brrValue,
                          validationNotesArray: prev[fieldId]?.validationNotesArray || [],
                          source: 'Manual'
                        }
                      }));
                    }
                  }}
                  className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded"
                >
                  Save
                </button>
                <button
                  onClick={() => {
                    setEditedValue(brrValue);
                    setIsEditing(false);
                  }}
                  className="px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : sources.length > 1 ? (
            <div className="grid grid-cols-2 gap-3">
              {sources.map((src, index) => {
                // If sourceValue is an object, get the value for this specific source
                // Otherwise, all sources share the same value
                const srcValue = typeof sourceValue === 'object' && !Array.isArray(sourceValue) && sourceValue !== null
                  ? (sourceValue[src] !== undefined ? sourceValue[src] : sourceValue)
                  : sourceValue;

                return (
                  <div
                    key={`${src}-${index}`}
                    className="p-3 bg-white rounded border border-blue-200 cursor-pointer hover:bg-blue-100 transition-colors"
                    onClick={() => handleSourceValueClick(typeof srcValue === 'object' ? String(srcValue) : srcValue)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSourceValueClick(typeof srcValue === 'object' ? String(srcValue) : srcValue); } }}
                    role="button"
                    tabIndex={0}
                    title="Click to use this value"
                  >
                    <div className="flex justify-end mb-2">
                      <span className="text-xs text-gray-500 font-medium mr-1">Source:</span>
                      <span className={`px-2 py-0.5 rounded border text-xs font-medium ${getSourceBadgeColor(src)}`}>
                        {src}
                      </span>
                    </div>
                    {isCheckbox ? (
                      <div className="flex items-center gap-2">
                        {srcValue ? (
                          <CheckSquare size={16} className="text-blue-600" />
                        ) : (
                          <Square size={16} className="text-gray-400" />
                        )}
                        <span className={srcValue ? 'text-blue-700 font-medium text-sm' : 'text-gray-500 text-sm'}>
                          {srcValue ? 'Yes' : 'No'}
                        </span>
                      </div>
                    ) : (
                      <div className={
                        String(srcValue).startsWith('No ') && String(srcValue).includes(' found')
                          ? "text-gray-400 text-xs break-words whitespace-normal"
                          : "text-gray-900 font-medium text-sm break-words whitespace-normal"
                      }>
                        {displayFormatter && typeof srcValue === 'string' ? displayFormatter(srcValue) : srcValue}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <>
              {sourceOnly && isEditing ? (
                /* This branch is now unreachable for sourceOnly+isEditing since it's handled above,
                   but keep for non-sourceOnly single-source case */
                <div className="space-y-2">
                  <textarea
                    value={String(editedValue)}
                    onChange={(e) => setEditedValue(e.target.value)}
                    className="w-full px-2 py-1 border border-blue-300 rounded text-sm focus:outline-none focus:border-blue-400 focus:ring-1 focus:ring-blue-400"
                    rows={4}
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setHasBeenEdited(true);
                        setIsEditing(false);
                        setShowNotes(true);

                        // Compliance will auto-recalculate via useMemo when fieldOverrides changes

                        if (setFieldOverrides) {
                          setFieldOverrides(prev => ({
                            ...prev,
                            [fieldId]: {
                              value: editedValue,
                              originalValue: brrValue,
                              validationNotesArray: prev[fieldId]?.validationNotesArray || [],
                              source: 'Manual'
                            }
                          }));
                        }
                      }}
                      className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded"
                    >
                      Save
                    </button>
                    <button
                      onClick={() => {
                        setEditedValue(brrValue);
                        setIsEditing(false);
                      }}
                      className="px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-xs rounded"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : isCheckbox ? (
                <div className="flex items-center gap-2">
                  {displaySourceValue ? (
                    <CheckSquare size={18} className="text-blue-600" />
                  ) : (
                    <Square size={18} className="text-gray-400" />
                  )}
                  <span className={displaySourceValue ? 'text-blue-700 font-medium' : 'text-gray-500'}>
                    {displaySourceValue ? 'Yes' : 'No'}
                  </span>
                </div>
              ) : (
                <div
                  {...(!sourceOnly && {
                    onClick: () => handleSourceValueClick(displaySourceValue),
                    onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleSourceValueClick(displaySourceValue); } },
                    role: "button",
                    tabIndex: 0,
                    className: "cursor-pointer hover:bg-blue-100 rounded p-1 transition-colors",
                    title: "Click to use this value"
                  })}
                >
                  <div className={
                    String(sourceOnly ? editedValue : displaySourceValue).startsWith('No ') &&
                    String(sourceOnly ? editedValue : displaySourceValue).includes(' found')
                      ? `text-gray-400 text-xs break-words ${sourceOnly ? 'whitespace-pre-line' : 'whitespace-normal'}`
                      : `text-gray-900 font-medium break-words ${sourceOnly ? 'whitespace-pre-line' : 'whitespace-normal'}`
                  }>
                    {(() => {
                      const val = sourceOnly ? editedValue : displaySourceValue;
                      return displayFormatter && typeof val === 'string' ? displayFormatter(val) : val;
                    })()}
                  </div>
                  {subtitle && (
                    <div className="mt-1 text-xs text-gray-500">{subtitle}</div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Compliance Dropdown and Notes */}
      <div className="flex items-center gap-3">
        <div className="flex-1">
          <label className="block text-xs font-medium text-gray-700 mb-1">Validation Status</label>
          <select
            value={compliance}
            onChange={(e) => handleComplianceChange(e.target.value as ComplianceStatus, true)}
            className={`w-full px-3 py-2 border rounded text-sm font-medium focus:outline-none focus:ring-2 bg-white ${
              compliance === 'compliant'
                ? 'border-green-300 text-green-700 focus:ring-green-200'
                : 'border-red-300 text-red-700 focus:ring-red-200'
            }`}
          >
            <option value="compliant" className="bg-white text-gray-900">Complies</option>
            <option value="non-compliant" className="bg-white text-gray-900">Does not correspond</option>
          </select>
        </div>

      </div>

      {/* Display Saved Validation Notes - Always Visible */}
      {validationNotesArray.length > 0 && (
        <div className="mt-3 space-y-2">
          <div className="flex items-center gap-2 mb-1">
            <div className="p-1.5 bg-indigo-100 rounded">
              <FileText size={14} className="text-indigo-600" />
            </div>
            <span className="text-xs font-bold text-indigo-900 uppercase tracking-wide">Validation Notes</span>
            <span className="text-xs text-gray-500">({validationNotesArray.length})</span>
          </div>
          {validationNotesArray.map((note, index) => (
            <div key={index} className="p-3 bg-gradient-to-r from-indigo-50 to-green-50 border-2 border-indigo-300 rounded-lg shadow-sm">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex items-center gap-1 text-xs text-indigo-600">
                      <Clock size={10} />
                      <span>{formatDateTime(note.timestamp)}</span>
                    </div>
                  </div>
                  <div className="text-sm text-gray-800 whitespace-pre-wrap bg-white/60 p-2 rounded border border-indigo-200">
                    {note.text}
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this validation note?')) {
                      const updatedNotesArray = validationNotesArray.filter((_, i) => i !== index);
                      setValidationNotesArray(updatedNotesArray);

                      // Update field overrides
                      setFieldOverrides(prev => {
                        const updated = { ...prev };
                        if (updatedNotesArray.length === 0) {
                          // If no notes left, remove validationNotesArray from override
                          if (updated[fieldId]) {
                            const { validationNotesArray, ...rest } = updated[fieldId];
                            if (Object.keys(rest).length > 0) {
                              updated[fieldId] = rest;
                            } else {
                              delete updated[fieldId];
                            }
                          }
                        } else {
                          // Otherwise, update with remaining notes
                          updated[fieldId] = {
                            ...updated[fieldId],
                            validationNotesArray: updatedNotesArray
                          };
                        }
                        return updated;
                      });
                    }
                  }}
                  className="p-1.5 hover:bg-red-100 rounded transition-colors text-red-600 hover:text-red-700"
                  title="Delete this note"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Validation Notes Button - Always Available in Grey */}
      <button
        onClick={() => setShowNotes(!showNotes)}
        className="mt-3 px-3 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded transition-colors flex items-center gap-1"
      >
        <FileText size={14} />
        Add Validation Notes
      </button>

      {/* Add New Validation Note Form - Shows Below Existing Notes */}
      {showNotes && (
        <div className="mt-3">
          <label className="block text-xs font-medium text-gray-700 mb-2">New Validation Note</label>
          <textarea
            value={currentNoteText}
            onChange={(e) => setCurrentNoteText(e.target.value)}
            placeholder="Enter any notes or comments about this field validation..."
            className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:outline-none focus:border-green-400 focus:ring-2 focus:ring-green-100"
            rows={3}
          />

          {/* Save Validation Note Button */}
          <div className="mt-2 space-y-2">
            <button
              onClick={() => {
                if (!currentNoteText.trim()) return;

                const newNote = {
                  text: currentNoteText.trim(),
                  timestamp: new Date().toISOString()
                };

                const updatedNotesArray = [...validationNotesArray, newNote];

                // Update local state
                setValidationNotesArray(updatedNotesArray);
                setCurrentNoteText('');

                // Save validation notes array to field overrides
                setFieldOverrides(prev => ({
                  ...prev,
                  [fieldId]: {
                    value: prev[fieldId]?.value || editedValue,
                    originalValue: prev[fieldId]?.originalValue || brrValue,
                    validationNotesArray: updatedNotesArray,
                    source: prev[fieldId]?.source || 'Manual'
                  }
                }));

                // Keep the form open so user can add more notes if needed
                // They can click "Add Validation Notes" again to close it
              }}
              disabled={!currentNoteText.trim()}
              className={`w-full py-2 font-medium rounded transition-all flex items-center justify-center gap-2 text-sm ${
                !currentNoteText.trim()
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              }`}
            >
              <FileText size={16} />
              Save Validation Note
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

ValidatedFieldWithCompliance.displayName = 'ValidatedFieldWithCompliance';
